import React from "react"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "./ui/dialog"
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { Label } from "./ui/label"
import { expenseApi, cardApi } from "../lib/api/accounting"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { getCompanyId } from "../lib/config"
import { Download } from "lucide-react"
import { toast } from "sonner"
import * as XLSX from "xlsx"

type CardCsvImportProps = {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function CardCsvImportModal({ open, onOpenChange }: CardCsvImportProps) {
  const [file, setFile] = React.useState<File | null>(null)
  const [rows, setRows] = React.useState<any[]>([])
  const [headers, setHeaders] = React.useState<string[]>([])
  const [mapping, setMapping] = React.useState<{ date?: string; description?: string; amount?: string }>(() => ({ }))
  const [companyId, setCompanyId] = React.useState<string>("")
  const [log, setLog] = React.useState<string>("")
  const [loading, setLoading] = React.useState(false)
  const [validationErrors, setValidationErrors] = React.useState<{
    fileFormat?: string
    columns?: string
  }>({})
  const qc = useQueryClient()
  
  // Template headers that must be present
  const templateHeaders = ['Date', 'Description', 'Amount']

  const parseCsv = async (fileObj: File) => {
    let header: string[] = []
    let data: string[][] = []
    
    // Check if it's an Excel file
    if (fileObj.name.endsWith('.xlsx') || fileObj.name.endsWith('.xls')) {
      try {
        // Read Excel file
        const arrayBuffer = await fileObj.arrayBuffer()
        const workbook = XLSX.read(arrayBuffer, { type: 'array' })
        
        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0]
        const worksheet = workbook.Sheets[firstSheetName]
        
        // Helper function to convert Excel serial date to JavaScript Date
        const excelDateToJSDate = (serial: number): Date => {
          // Excel epoch is January 1, 1900, but Excel incorrectly treats 1900 as a leap year
          // So we need to adjust: Excel day 1 = Jan 1, 1900, but we subtract 1 day
          const excelEpoch = new Date(1899, 11, 30); // Dec 30, 1899 (day 0 in Excel)
          const jsDate = new Date(excelEpoch.getTime() + serial * 86400000); // 86400000 ms per day
          return jsDate;
        };
        
        // Helper function to format date
        const formatDate = (value: any): string => {
          if (value instanceof Date) {
            return value.toISOString().split('T')[0]; // YYYY-MM-DD format
          }
          if (typeof value === 'number') {
            // Check if it's an Excel serial date
            // Excel dates are typically between 1 (Jan 1, 1900) and ~100000 (year 2174)
            // For practical purposes, dates from 1900-2100 would be 1-73050
            if (value >= 1 && value <= 100000) {
              try {
                const date = excelDateToJSDate(value);
                // Validate the date is reasonable (between 1900 and 2100)
                const year = date.getFullYear();
                if (year >= 1900 && year <= 2100) {
                  return date.toISOString().split('T')[0];
                }
              } catch (e) {
                // Fall through to return as string
              }
            }
            return String(value);
          }
          // Try to parse as date string
          if (typeof value === 'string' && value.trim() !== '') {
            // Try various date formats
            const parsed = new Date(value);
            if (!isNaN(parsed.getTime())) {
              return parsed.toISOString().split('T')[0];
            }
            // Try parsing as Excel date string (e.g., "2/28/2024")
            const dateMatch = value.match(/(\d{1,2})\/(\d{1,2})\/(\d{4})/);
            if (dateMatch) {
              const [, month, day, year] = dateMatch;
              const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
              if (!isNaN(date.getTime())) {
                return date.toISOString().split('T')[0];
              }
            }
          }
          return String(value || '');
        };
        
        // Find the Date column index from header
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
        const firstRow: any[] = [];
        for (let C = range.s.c; C <= range.e.c; ++C) {
          const cellAddress = XLSX.utils.encode_cell({ r: range.s.r, c: C });
          const cell = worksheet[cellAddress];
          firstRow.push(cell ? (cell.w || cell.v || '') : '');
        }
        const dateColumnIndex = firstRow.findIndex((h: any) => 
          String(h || '').toLowerCase().trim() === 'date'
        );
        
        // Convert to JSON with header row, using raw values to properly detect dates
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { 
          header: 1, 
          defval: '',
          raw: true // Use raw values to detect Excel serial dates
        }) as any[][]
        
        if (jsonData.length === 0) {
          setValidationErrors({ fileFormat: 'Excel file is empty' })
          return
        }
        
        // First row is header - filter out empty header cells
        header = jsonData[0].map((h: any) => String(h || '').trim()).filter(h => h !== '')
        
        if (header.length === 0) {
          setValidationErrors({ fileFormat: 'Excel file has no valid header row' })
          return
        }
        
        // Rest is data - filter out completely empty rows and pad rows to match header length
        // Also convert dates in the Date column
        data = jsonData.slice(1)
          .filter(row => row.some((cell: any) => cell !== null && cell !== undefined && String(cell).trim() !== ''))
          .map(row => {
            const paddedRow = [...row]
            // Pad row to match header length
            while (paddedRow.length < header.length) {
              paddedRow.push('')
            }
            return paddedRow.slice(0, header.length).map((cell: any, idx: number) => {
              // If this is the Date column, format the date properly
              if (idx === dateColumnIndex && cell !== null && cell !== undefined && cell !== '') {
                return formatDate(cell);
              }
              // For other columns, convert to string but preserve numbers
              if (typeof cell === 'number') {
                return String(cell);
              }
              return String(cell || '');
            });
          })
      } catch (error: any) {
        setValidationErrors({ 
          fileFormat: `Error reading Excel file: ${error?.message || 'Invalid file format'}` 
        })
        return
      }
    } else {
      // CSV file
      const text = await fileObj.text()
      const lines = text.split(/\r?\n/).filter(Boolean)
      if (lines.length === 0) {
        setValidationErrors({ fileFormat: 'File is empty' })
        return
      }
      // naive CSV splitting with quotes handling (simple)
      const parseLine = (l: string) => {
        const out: string[] = []
        let cur = ''
        let inQuotes = false
        for (let i = 0; i < l.length; i++) {
          const ch = l[i]
          if (ch === '"') {
            if (inQuotes && l[i+1] === '"') { cur += '"'; i++ } else { inQuotes = !inQuotes }
          } else if (ch === ',' && !inQuotes) { out.push(cur); cur = '' } else { cur += ch }
        }
        out.push(cur)
        return out
      }
      header = parseLine(lines[0]).map(h => h.trim())
      data = lines.slice(1).map(parseLine)
    }
    
    // Detect format: Excel format (Date, Category, Item, Quantity, Unit price, Total price) or Standard format
    const headerLower = header.map(h => h.toLowerCase())
    const hasItem = headerLower.includes('item')
    const hasCategory = headerLower.includes('category')
    const hasTotalPrice = headerLower.includes('total price') || headerLower.includes('totalprice')
    const hasUnitPrice = headerLower.includes('unit price') || headerLower.includes('unitprice')
    const hasQuantity = headerLower.includes('quantity')
    const hasAmount = headerLower.includes('amount')
    const hasDescription = headerLower.includes('description')
    
    // Excel format: has Item column (and optionally Category, Quantity, Unit price, Total price)
    // Standard format: has Date, Description, Amount
    const isExcelFormat = hasItem || (hasCategory && (hasTotalPrice || hasUnitPrice || hasQuantity))
    
    // Validate file format
    const errors: { fileFormat?: string; columns?: string } = {}
    
    if (isExcelFormat) {
      // Excel format: Requires Date and Item, Amount/Total price is optional (can calculate from Quantity * Unit price)
      const excelRequired = ['date', 'item']
      const missingColumns = excelRequired.filter(req => !headerLower.includes(req))
      
      if (missingColumns.length > 0) {
        errors.fileFormat = `Excel format requires: Date and Item columns`
        errors.columns = `Missing columns: ${missingColumns.join(', ')}`
      } else {
        // Check if we have a way to calculate amount
        const hasAmountSource = hasTotalPrice || hasAmount || (hasQuantity && hasUnitPrice)
        if (!hasAmountSource) {
          errors.fileFormat = `Excel format requires at least one of: Total price, Amount, or both Quantity and Unit price`
          errors.columns = `Missing amount calculation: Need Total price, Amount, or (Quantity + Unit price)`
        }
      }
    } else {
      // Standard format: Date, Description, Amount (Payment Method is optional)
      const requiredColumns = templateHeaders.map(h => h.toLowerCase())
      const missingColumns = requiredColumns.filter(req => !headerLower.includes(req))
      
      if (missingColumns.length > 0) {
        errors.fileFormat = `File must contain required columns: ${templateHeaders.join(', ')}`
        errors.columns = `Missing required columns: ${missingColumns.map(c => templateHeaders[requiredColumns.indexOf(c)]).join(', ')}. Optional columns: Payment Method, Category, Quantity, Unit price, Total price`
      }
    }
    
    setValidationErrors(errors)
    
    if (Object.keys(errors).length === 0) {
      setHeaders(header)
      const rs = data.map(cols => Object.fromEntries(header.map((h, idx) => [h, cols[idx] ?? ''])))
      setRows(rs)
      
      if (isExcelFormat) {
        // Auto-map Excel format - find amount column with priority: Total price > Amount > (will calculate from Quantity * Unit price)
        const totalPriceCol = header.find(h => h.toLowerCase() === 'total price' || h.toLowerCase() === 'totalprice')
        const amountCol = header.find(h => h.toLowerCase() === 'amount')
        const amountColumn = totalPriceCol || amountCol || null // Will calculate from Quantity * Unit price if null
        
        setMapping({ 
          date: header.find(h => h.toLowerCase() === 'date') || header[0],
          description: header.find(h => h.toLowerCase() === 'item') || header.find(h => h.toLowerCase() === 'description') || header[2],
          amount: amountColumn || header[header.length - 1] // Fallback to last column
        })
      } else {
        // Auto-map standard format (Date, Description, Amount, Payment Method optional)
        setMapping({ 
          date: header.find(h => h.toLowerCase() === 'date') || header[0], 
          description: header.find(h => h.toLowerCase() === 'description') || header[1], 
          amount: header.find(h => h.toLowerCase() === 'amount') || header[2] 
        })
      }
    } else {
      setHeaders([])
      setRows([])
      setMapping({})
    }
  }

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0]
    if (f) { 
      setFile(f)
      setValidationErrors({}) // Clear previous errors
      parseCsv(f) 
    }
  }
  
  // Reset validation errors when modal closes
  React.useEffect(() => {
    if (!open) {
      setValidationErrors({})
      setFile(null)
      setRows([])
      setHeaders([])
      setMapping({})
      setLog('')
    }
  }, [open])

  // Get company ID from localStorage/config
  React.useEffect(() => {
    try {
      const c = getCompanyId()
      if (c) setCompanyId(c)
    } catch {}
  }, [])


  const importMutation = useMutation({
    mutationFn: async (payload: any[]) => {
      // Detect if this is Excel format by checking if rows have Excel column names
      const isExcelFormat = payload.some(r => r.Item || r.item || r.Category || r.category || r['Total price'] || r['Unit price'])
      
      if (isExcelFormat) {
        // Excel format: Map Date, Category, Item, Quantity, Unit price, Total price, Payment Method
        return expenseApi.importExpenses(
          payload.map((r) => ({
            Date: String(r.Date || r.date || ''),
            Category: String(r.Category || r.category || ''),
            Item: String(r.Item || r.item || r.Description || r.description || ''),
            Quantity: r.Quantity || r.quantity || null,
            'Unit price': r['Unit price'] || r['unit price'] || r['Unit Price'] || r.unitPrice || null,
            // Total price: prefer Total price, fallback to Amount, or will be calculated from Quantity * Unit price
            'Total price': r['Total price'] || r['Total Price'] || r.totalPrice || r.Amount || r.amount || null,
            Vendor: r.Vendor || r.vendor || r.vendorName || null,
            Notes: r.Notes || r.notes || null,
            Department: r.Department || r.department || null,
            Project: r.Project || r.project || null,
            ReferenceNumber: r.ReferenceNumber || r.referenceNumber || r.reference || null,
            'Payment Method': r['Payment Method'] || r['PaymentMethod'] || r['Payment'] || r['Method'] || 
                            r.PaymentMethod || r.paymentMethod || r.payment || r.method || null,
            Currency: r.Currency || r.currency || 'RWF'
          })),
          undefined, // No default category - intelligent detection will handle it
          companyId,
          'cash' // default payment method
        )
      } else {
        // Standard format - map Date, Description, Amount, Payment Method, Quantity, Unit price
        return expenseApi.importExpenses(
          payload.map((r) => ({
            Date: String(r.Date || r.date || ''),
            Description: String(r.Description || r.description || ''),
            Amount: Number(r.Amount || r.amount || 0),
            'Payment Method': r['Payment Method'] || r['PaymentMethod'] || r['Payment'] || r['Method'] || 
                            r.PaymentMethod || r.paymentMethod || r.payment || r.method || null,
            Quantity: r.Quantity || r.quantity || null,
            'Unit price': r['Unit price'] || r['unit price'] || r['Unit Price'] || r.unitPrice || null,
            Vendor: r.Vendor || r.vendor || r.vendorName || null,
            Notes: r.Notes || r.notes || null,
            Department: r.Department || r.department || null,
            Project: r.Project || r.project || null,
            ReferenceNumber: r.ReferenceNumber || r.referenceNumber || r.reference || null,
            Currency: r.Currency || r.currency || 'RWF'
          })),
          undefined, // No default category - intelligent detection will handle it
          companyId,
          'cash' // default payment method
        )
      }
    },
    onSuccess: (res) => {
      const successCount = res.created || res.summary?.successful || 0
      const errorCount = res.summary?.failed || 0
      const createdCategories = res.createdCategories || []
      
      let message = `Successfully imported ${successCount} expenses.`
      if (createdCategories.length > 0) {
        message += ` Created ${createdCategories.length} new categor${createdCategories.length === 1 ? 'y' : 'ies'}: ${createdCategories.slice(0, 5).join(', ')}${createdCategories.length > 5 ? '...' : ''}`
      }
      
      if (errorCount > 0) {
        toast.warning(`Imported ${successCount} expenses. ${errorCount} failed.`, {
          description: res.errors?.slice(0, 3).map((e: any) => `Row ${e.row}: ${e.error}`).join(', ')
        })
      } else {
        toast.success(message)
      }
      qc.invalidateQueries({ queryKey: ['expenses'] })
      qc.invalidateQueries({ queryKey: ['expense-categories'] })
      // Close modal immediately after import
      onOpenChange(false)
    },
    onError: (err: any) => {
      const errorMsg = err?.response?.data?.message || err?.message || 'Unknown error'
      setLog(`Import failed: ${errorMsg}`)
      toast.error(`Import failed: ${errorMsg}`)
    },
    onSettled: () => setLoading(false)
  })

  const importRows = async () => {
    // Clear previous errors
    const errors: { category?: string; fileFormat?: string; columns?: string } = {}
    
    // Validate that file has been loaded and mapped
    if (!companyId || !mapping.date || !mapping.description || !mapping.amount || rows.length === 0) {
      errors.columns = 'Please upload a valid CSV file with Date, Description, and Amount columns'
    }
    
    // Category is optional - expenses without categories will be categorized as "Uncategorized"
    // No validation error needed for missing category
    
    setValidationErrors(errors)
    
    // If there are errors, don't proceed
    if (Object.keys(errors).length > 0) {
      return
    }
    
    setLoading(true)
    // Map rows to include payment method, quantity, and unit price if available
    const mapped = rows.map((r) => {
      const baseRow: any = {
        Date: String(r[mapping.date!]),
        Description: String(r[mapping.description!]),
        Amount: parseFloat(String(r[mapping.amount!]).replace(/[^0-9.-]/g, ''))
      }
      
      // Add payment method if column exists
      const paymentMethodCol = headers.find(h => 
        h.toLowerCase() === 'payment method' || 
        h.toLowerCase() === 'paymentmethod' || 
        h.toLowerCase() === 'payment' || 
        h.toLowerCase() === 'method'
      )
      if (paymentMethodCol && r[paymentMethodCol]) {
        baseRow['Payment Method'] = String(r[paymentMethodCol])
      }
      
      // Add quantity if column exists
      const quantityCol = headers.find(h => 
        h.toLowerCase() === 'quantity' || 
        h.toLowerCase() === 'qty'
      )
      if (quantityCol && r[quantityCol]) {
        const qtyValue = parseFloat(String(r[quantityCol]).replace(/[^0-9.-]/g, ''))
        if (!isNaN(qtyValue) && qtyValue >= 0) {
          baseRow.Quantity = qtyValue
        }
      }
      
      // Add unit price if column exists
      const unitPriceCol = headers.find(h => 
        h.toLowerCase() === 'unit price' || 
        h.toLowerCase() === 'unitprice' || 
        h.toLowerCase() === 'unit price' ||
        h.toLowerCase() === 'price per unit'
      )
      if (unitPriceCol && r[unitPriceCol]) {
        const upValue = parseFloat(String(r[unitPriceCol]).replace(/[^0-9.-]/g, ''))
        if (!isNaN(upValue) && upValue >= 0) {
          baseRow['Unit price'] = upValue
        }
      }
      
      return baseRow
    })
    importMutation.mutate(mapped as any)
  }

  // Download Excel format template
  const downloadExcelTemplate = () => {
    const headers = ['Date', 'Category', 'Item', 'Quantity', 'Unit price', 'Total price', 'Payment Method']
    
    const sampleRows: string[][] = [
      ['2/28/2024', 'Office Supplies', 'CLEARING FEES', '1', '720000', '720000', 'Cash'],
      ['6/2/2023', 'Staff Welfare', 'RED BULL 250ML*cans', '24', '1915', '45960', 'Cash'],
      ['10/7/2024', 'Taxes & Fees', 'PARKING FEES / WAITING CONTAINER', '1', '11800', '11800', 'Cash'],
      ['10/7/2024', 'Fuel & Transportation', 'PMS', '6', '80000', '480000', 'Bank Transfer'],
      ['10/7/2024', 'Repair & Maintenance', 'CEMENT 42.5', '20', '1528', '30560', 'Cash'],
      ['10/7/2024', 'Fuel & Transportation', 'Parking 20FT', '26', '1544', '40144', 'Cash'],
      ['10/7/2024', 'Fuel & Transportation', 'DIESEL (GASOIL)', '620', '12500', '7750000', 'Bank Transfer'],
      ['10/7/2024', 'Repair & Maintenance', 'HIGH GLOSS THINNER', '1', '50000', '50000', 'Cash'],
      ['10/7/2024', 'Fuel & Transportation', 'MAZOUT', '40', '14000', '560000', 'Cash'],
      ['10/7/2024', 'Staff Welfare', 'milk 1L', '2', '386554', '773108', 'Cash'],
      ['10/7/2024', 'Staff Welfare', 'MILK INYANGE', '48', '270588', '12988224', 'Cash'],
      ['10/7/2024', 'Staff Welfare', 'juice1L', '10', '35400', '354000', 'Cash'],
      ['10/7/2024', 'Taxes & Fees', 'Handling and Cargo Storage', '1', '954880', '954880', 'Bank Transfer'],
      ['10/7/2024', 'Staff Welfare', 'Mukamira 250ml', '1', '28000', '28000', 'Cash'],
      ['10/7/2024', 'Staff Welfare', 'HADAY LIGHT SOY SAUCE 1.9L', '1', '195000', '195000', 'Cash'],
      ['10/7/2024', 'Staff Welfare', 'BEVERAGE 3 500ML', '1', '19000', '19000', 'Cash'],
      ['10/7/2024', 'Staff Welfare', 'FISH TAIL', '24', '1958', '46992', 'Cash'],
      ['10/7/2024', 'Staff Welfare', 'GREEN TEA 500ML', '48', '2208', '105984', 'Cash']
    ]

    const csvContent = [
      headers.join(','),
      ...sampleRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'expense-import-excel-template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success('Downloaded Excel format template with sample data')
  }

  // Download CSV template function - matches backend validation
  const downloadTemplate = () => {
    // Template with payment method included
    const headers = ['Date', 'Description', 'Amount', 'Payment Method']
    
    // Generate many sample rows with varied data including payment methods
    const sampleRows: string[][] = [
      ['2024-01-15', 'Office Supplies Purchase', '125.50', 'Cash'],
      ['2024-01-18', 'Team Lunch Meeting', '89.75', 'Credit Card'],
      ['2024-01-22', 'Software Subscription', '199.00', 'Bank Transfer'],
      ['2024-01-25', 'Travel - Hotel Booking', '350.00', 'Credit Card'],
      ['2024-01-28', 'Conference Registration', '500.00', 'Bank Transfer'],
      ['2024-02-01', 'Internet Service', '79.99', 'Bank Transfer'],
      ['2024-02-05', 'Marketing Materials', '225.00', 'Cash'],
      ['2024-02-08', 'Client Dinner', '156.30', 'Credit Card'],
      ['2024-02-12', 'Equipment Maintenance', '175.00', 'Check'],
      ['2024-02-15', 'Cloud Storage Subscription', '49.99', 'Bank Transfer'],
      ['2024-02-20', 'Taxi to Airport', '45.00', 'Cash'],
      ['2024-02-25', 'Office Furniture', '850.00', 'Bank Transfer'],
      ['2024-03-01', 'Phone Bill', '65.00', 'Bank Transfer'],
      ['2024-03-05', 'Training Course', '299.00', 'Credit Card'],
      ['2024-03-10', 'Marketing Campaign', '450.00', 'Bank Transfer'],
      ['2024-03-15', 'Client Meeting Coffee', '12.50', 'Cash'],
      ['2024-03-20', 'Software License Renewal', '350.00', 'Bank Transfer'],
      ['2024-03-25', 'Office Cleaning Service', '120.00', 'Cash'],
      ['2024-03-28', 'Conference Parking', '25.00', 'Cash'],
      ['2024-04-01', 'Monthly Software Subscription', '99.99', 'Bank Transfer'],
      ['2024-04-05', 'Team Building Activity', '280.00', 'Credit Card'],
      ['2024-04-10', 'Office Supplies', '95.00', 'Cash'],
      ['2024-04-15', 'Client Lunch', '78.50', 'Credit Card'],
      ['2024-04-20', 'Website Hosting', '125.00', 'Bank Transfer'],
      ['2024-04-25', 'Professional Development', '225.00', 'Bank Transfer'],
      ['2024-04-30', 'Marketing Materials Printing', '175.00', 'Cash'],
      ['2024-05-05', 'Business Mileage', '67.00', 'Cash'],
      ['2024-05-10', 'Client Dinner', '145.00', 'Credit Card'],
      ['2024-05-15', 'Office Rent', '2500.00', 'Bank Transfer'],
      ['2024-05-20', 'Electricity Bill', '185.00', 'Bank Transfer'],
      ['2024-05-25', 'Employee Training Workshop', '450.00', 'Bank Transfer'],
      ['2024-05-30', 'Office Equipment Repair', '225.00', 'Check'],
      ['2024-06-01', 'Insurance Premium', '350.00', 'Bank Transfer'],
      ['2024-06-05', 'Advertising Campaign', '500.00', 'Bank Transfer'],
      ['2024-06-10', 'Business Cards', '85.00', 'Cash'],
      ['2024-06-15', 'Client Meeting Lunch', '125.00', 'Credit Card'],
      ['2024-06-20', 'Data Backup Service', '75.00', 'Bank Transfer'],
      ['2024-06-25', 'Legal Consultation', '300.00', 'Check'],
      ['2024-06-30', 'Office Supplies Restock', '150.00', 'Cash'],
      ['2024-07-01', 'Internet Service', '79.99', 'Bank Transfer'],
      ['2024-07-05', 'Marketing Materials', '225.00', 'Cash'],
      ['2024-07-10', 'Software Subscription', '199.00', 'Bank Transfer'],
      ['2024-07-15', 'Office Supplies', '125.50', 'Cash'],
      ['2024-07-20', 'Team Lunch', '89.75', 'Credit Card'],
      ['2024-07-25', 'Travel Expenses', '350.00', 'Credit Card'],
      ['2024-07-30', 'Training Course', '299.00', 'Bank Transfer']
    ]

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...sampleRows.map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
    ].join('\n')

    // Create and download blob
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)
    link.setAttribute('href', url)
    link.setAttribute('download', 'expense-import-template.csv')
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    toast.success('Downloaded CSV template with sample data')
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Import Expenses (CSV/Excel)</DialogTitle>
          <DialogDescription>
            Import expenses from CSV or Excel files. Supports standard format (Date, Description, Amount) or Excel format (Date, Category, Item, Quantity, Unit price, Total price, Payment Method).
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {/* Download Template Section */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-center justify-between mb-3">
              <Label className="text-sm font-semibold">Download Template</Label>
            </div>
            <p className="text-xs text-muted-foreground mb-3">
              Download a template file with sample data. Choose between standard format or Excel format with all columns.
            </p>
            <div className="grid grid-cols-2 gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadTemplate}
                className="hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700"
              >
                <Download className="w-4 h-4 mr-2" />
                CSV Format
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={downloadExcelTemplate}
                className="hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Excel Format
              </Button>
            </div>
            <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs">
              <p className="font-semibold text-blue-900 mb-1">Supported Formats:</p>
              <ul className="text-blue-800 space-y-0.5">
                <li>• <strong>CSV Format:</strong> <span className="text-red-600">Date, Description, Amount</span> (required) | <span className="text-green-600">Payment Method</span> (optional)</li>
                <li>• <strong>Excel Format:</strong> <span className="text-red-600">Date, Item</span> (required) | <span className="text-green-600">Category, Quantity, Unit price, Total price, Payment Method</span> (optional)</li>
              </ul>
              <p className="text-blue-700 mt-2 italic">Note: Payment Method column is optional. If not provided, defaults to 'cash'.</p>
            </div>
          </div>

          <div>
            <Label>Company</Label>
            <Input value={companyId} disabled className="bg-slate-50 border border-slate-300" placeholder="Auto-detected from header" />
            <p className="text-xs text-slate-500 mt-1">
              Categories are automatically detected from item descriptions using intelligent matching. Missing categories will be auto-created.
            </p>
          </div>
          <div>
            <Label className={validationErrors.fileFormat ? 'text-red-600' : ''}>
              Import File (CSV/Excel) {validationErrors.fileFormat && '*'}
            </Label>
            <Input 
              type="file" 
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" 
              onChange={(e) => {
                onFile(e)
                // Clear file format errors when new file is selected
                if (validationErrors.fileFormat || validationErrors.columns) {
                  setValidationErrors(prev => {
                    const newErrors = { ...prev }
                    delete newErrors.fileFormat
                    delete newErrors.columns
                    return newErrors
                  })
                }
              }}
              className={validationErrors.fileFormat ? 'border-red-500 border-2' : 'border border-slate-300 hover:border-teal-300'}
            />
            {validationErrors.fileFormat && (
              <p className="text-xs text-red-600 mt-1">{validationErrors.fileFormat}</p>
            )}
            {validationErrors.columns && (
              <p className="text-xs text-red-600 mt-1">{validationErrors.columns}</p>
            )}
            <p className="text-xs text-slate-500 mt-1">
              Supports CSV and Excel files (.csv, .xlsx, .xls). Categories are auto-detected from item descriptions. Payment method defaults to "cash" if not specified.
            </p>
            <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs">
              <p className="font-semibold text-amber-900 mb-1">💡 Tips:</p>
              <ul className="text-amber-800 space-y-0.5">
                <li>• Excel files: Directly supported (.xlsx, .xls) - no conversion needed</li>
                <li>• Categories: Auto-detected from item names (e.g., "milk" → Staff Welfare)</li>
                <li>• Payment Method: Optional column - defaults to "cash" if missing</li>
                <li>• Missing categories: Will be auto-created or use "Uncategorized"</li>
              </ul>
            </div>
          </div>
          {rows.length > 0 && (
            <div className="border rounded">
              <div className="p-2 text-sm text-muted-foreground">Preview ({rows.length} rows)</div>
              <div className="max-h-64 overflow-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr>
                      {(headers || []).map(h => (<th key={h} className="p-2 text-left bg-muted">{h}</th>))}
                    </tr>
                  </thead>
                  <tbody>
                    {rows.slice(0,20).map((r, idx) => (
                      <tr key={idx} className="border-b">
                        {headers.map(h => (<td key={h} className="p-2">{r[h]}</td>))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="flex justify-end gap-2">
            <Button 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              disabled={loading}
              className="hover:bg-teal-50 hover:border-teal-300 hover:text-teal-700"
            >
              Cancel
            </Button>
            <Button 
              onClick={importRows} 
              disabled={!rows.length || !companyId || loading}
              className="bg-teal-600 hover:bg-teal-700 text-white"
            >
              {loading ? 'Importing…' : 'Import'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}


